import { BehaviorSubject, Observable } from 'rxjs';
import { isDefined } from '@fi-shared/utils/is-defined';

export interface ISelectSearchSelectedItemChange<T> {
    item: T;
    isSelected: boolean;
}

export interface ISelectedItemChange<T, TKey> {
    selectedItemIds: TKey[];
    changedItem: T;
    isSelected: boolean;
    selectParentWithOnlyChildSelected?: boolean;
    skipChildren?: boolean;
}

export interface IFlatItemData<T, TKey> {
    id: TKey; // hierarchical data can contain different items - areas, units, clusters, so here we can have composite key, for example ObjectId + '@' + 'ObjectType'
    name: string;
    childItems: T[];
}

export interface IFlatItem<T, TKey = number> {
    Id: TKey;
    Name: string;

    ParentId: TKey;
    Level: number;
    sourceItem: T;
}

export class FiHierarchicalHelper<T, TKey = number> {
    private _flatItems$ = new BehaviorSubject<IFlatItem<T, TKey>[]>([]);
    private readonly transformFn: (item: T) => IFlatItemData<T, TKey>;

    constructor(transformFn: (item: T) => IFlatItemData<T, TKey>) {
        this.transformFn = transformFn;
    }

    setFlatItems(items: T[]) {
        this._flatItems$.next(this.getTransformedItems(items, 0));
    }

    private getTransformedItems(items: T[], level: number, parent?: IFlatItem<T, TKey>): IFlatItem<T, TKey>[] {
        let transformedItems: IFlatItem<T, TKey>[] = [];
        items.forEach((item: T) => {
            const transformedData = this.transformFn(item);
            const transformedItem: IFlatItem<T, TKey> = {
                Id: transformedData.id,
                Name: transformedData.name,
                Level: level,
                ParentId: parent?.Id,
                sourceItem: Object.assign({}, item, { ChildItems: undefined }), // or just sourceItem: item
            };

            transformedItems.push(transformedItem);
            transformedItems.push(...this.getTransformedItems(transformedData.childItems, level + 1, transformedItem));
        });
        return transformedItems;
    }

    get flatItems$() {
        return this._flatItems$.asObservable();
    }
    get flatItems() {
        return this._flatItems$.value;
    }

    onSelectedItemChange($event: ISelectedItemChange<IFlatItem<T, TKey>, TKey>) {
        const changedItems = this._flatItems$.value.filter((flatItem) => {
            return flatItem.Id === $event.changedItem.Id;
        });

        changedItems.forEach((item) => {
            if (!$event.skipChildren) {
                // note: set all children same state as element has
                const allChildren = this.getAllChildren(item.Id);
                allChildren.forEach((child: IFlatItem<T, TKey>) => {
                    $event.changedItem = child;
                    this.setChildrenState($event);
                });
            }

            // note: check if siblings are selected -> select/deselect parent, repeat with next parent
            const parentItem = this._flatItems$.value.find((flatItem) => {
                return flatItem.Id === item.ParentId;
            });

            if (isDefined(parentItem)) {
                const isParentSelect: boolean = !!$event.selectedItemIds.find((selectedId: TKey) => {
                    return selectedId === parentItem.Id;
                });
                const allSiblingsIds: TKey[] = this.getAllChildrenIds(parentItem.Id);
                // using Set to remove duplicates
                const selectedSiblings = Array.from(
                    new Set(this.getSelectedChildrenIds($event.selectedItemIds, allSiblingsIds)),
                );
                if ($event.selectParentWithOnlyChildSelected) {
                    if (selectedSiblings.length) $event.selectedItemIds.push(parentItem.Id);
                }
                if (selectedSiblings.length === allSiblingsIds.length && !isParentSelect) {
                    $event.selectedItemIds.push(parentItem.Id);
                }

                if (!selectedSiblings.length && isParentSelect) {
                    $event.selectedItemIds = $event.selectedItemIds.filter((selectedId: TKey) => {
                        return selectedId !== parentItem.Id;
                    });
                }

                if (!!parentItem.ParentId) {
                    $event.skipChildren = true;
                    $event.changedItem = parentItem;
                    $event.selectedItemIds = this.onSelectedItemChange($event);
                }
            }
        });

        return $event.selectedItemIds;
    }

    isIndeterminate(selectedItemIds: TKey[], item: IFlatItem<T, TKey>): boolean {
        const childrenIds = this.getAllChildrenIds(item.Id);
        if (!childrenIds.length) return false;

        const selectedChildrenIds: TKey[] = this.getSelectedChildrenIds(selectedItemIds, childrenIds);
        if (!selectedChildrenIds.length) return false;

        return selectedChildrenIds.length !== childrenIds.length;
    }

    isSelected(selectedItemIds: TKey[], item: IFlatItem<T, TKey>): boolean {
        return !this.isIndeterminate(selectedItemIds, item) && selectedItemIds.includes(item.Id);
    }

    private getAllChildren(itemId: TKey): IFlatItem<T, TKey>[] {
        return this._flatItems$.value.filter((item: IFlatItem<T, TKey>) => item.ParentId === itemId);
    }

    private getAllChildrenIds(itemId: TKey): TKey[] {
        return this.getAllChildren(itemId).map((child: IFlatItem<T, TKey>) => child.Id);
    }

    private getSelectedChildrenIds(selectedItemIds: TKey[], childrenIds: TKey[]) {
        return selectedItemIds.filter((selectedItemId) => childrenIds.includes(selectedItemId));
    }

    private setChildrenState($event: ISelectedItemChange<IFlatItem<T, TKey>, TKey>) {
        this._flatItems$.value
            .filter((item) => item.Id === $event.changedItem.Id)
            .forEach(() => {
                const childIndex: number = $event.selectedItemIds.findIndex((selectedItemId) => {
                    return selectedItemId === $event.changedItem.Id;
                });

                if (childIndex !== -1 && !$event.isSelected) {
                    $event.selectedItemIds.splice(childIndex, 1);
                }

                if (childIndex === -1 && $event.isSelected) {
                    $event.selectedItemIds.push($event.changedItem.Id);
                }

                if (
                    this._flatItems$.value.findIndex(
                        (flatItem: IFlatItem<T, TKey>) => flatItem.ParentId === $event.changedItem.Id,
                    ) !== -1
                ) {
                    this.onSelectedItemChange($event);
                }
            });
    }
}
